/// <amd-module name="Environments/Providers/External/Kubernetes/WebUI/Workloads/DeploymentsTable" />
/*
    Copyright (c) Microsoft Corporation. All rights reserved.
    Licensed under the MIT license.
*/

import { V1Deployment, V1DeploymentList, V1ObjectMeta, V1Pod, V1ReplicaSet, V1ReplicaSetList } from "@kubernetes/client-node";
import { Ago } from "VSSUI/Ago";
import { CardContent, CustomCard } from "VSSUI/Card";
import { ObservableValue } from "VSS/Core/Observable";
import { equals, format, localeFormat } from "VSS/Core/Util/String";
import { CustomHeader, HeaderDescription, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { ITableColumn, ITableRow, Table, ITableProps } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { css } from "VSSUI/Util";
import * as React from "react";
import * as Resources from "Environments/Providers/External/Kubernetes/Resources";
import { defaultColumnRenderer, onPodsColumnClicked, renderPodsStatusTableCell, renderTableCell } from "../Common/KubeCardWithTable";
import { Tags } from "../Common/Tags";
import { ImageDetailsEvents, SelectedItemKeys, WorkloadsEvents } from "../Constants";
import { ActionsCreatorManager } from "../FluxCommon/ActionsCreatorManager";
import { StoreManager } from "../FluxCommon/StoreManager";
import { ImageDetailsStore } from "../ImageDetails/ImageDetailsStore";
import { KubeFactory } from "../KubeFactory";
import { PodsStore } from "../Pods/PodsStore";
import { SelectionActionsCreator } from "../Selection/SelectionActionCreator";
import { ISelectionPayload } from "../Selection/SelectionActions";
import { IDeploymentReplicaSetItem, IDeploymentReplicaSetMap, IVssComponentProperties } from "../Types";
import { Utils } from "../Utils";
import { WorkloadsActionsCreator } from "./WorkloadsActionsCreator";
import { WorkloadsStore } from "./WorkloadsStore";
import { ImageDetailsActionsCreator } from "../ImageDetails/ImageDetailsActionsCreator";
import { IImageDetails } from "../../Contracts/Types";
import { PillGroupOverflow } from "VSSUI/PillGroup";

export interface IDeploymentsTableProperties extends IVssComponentProperties {
    nameFilter?: string;
}

export interface IDeploymentsTableState {
    deploymentList?: V1DeploymentList;
    replicaSetList?: V1ReplicaSetList;
}

export class DeploymentsTable extends React.Component<IDeploymentsTableProperties, IDeploymentsTableState> {
    constructor(props: IDeploymentsTableProperties) {
        super(props, {});
        this._workloadsActionCreator = ActionsCreatorManager.GetActionCreator<WorkloadsActionsCreator>(WorkloadsActionsCreator);
        this._selectionActionCreator = ActionsCreatorManager.GetActionCreator<SelectionActionsCreator>(SelectionActionsCreator);
        this._imageDetailsStore = StoreManager.GetStore<ImageDetailsStore>(ImageDetailsStore);
        this._store = StoreManager.GetStore<WorkloadsStore>(WorkloadsStore);

        this._workloadsActionCreator.getReplicaSets(KubeFactory.getKubeService());

        this.state = { deploymentList: undefined, replicaSetList: undefined };

        this._store.addListener(WorkloadsEvents.ReplicaSetsFetchedEvent, this._onReplicaSetsFetched);
        this._imageDetailsStore.addListener(ImageDetailsEvents.HasImageDetailsEvent, this._setHasImageDetails);
    }


    public render(): React.ReactNode {
        const deployments = this.state.deploymentList;
        const filteredDeployments: V1Deployment[] = (deployments && deployments.items || []).filter((deployment) => {
            return Utils.filterByName(deployment.metadata.name, this.props.nameFilter);
        });

        if (filteredDeployments.length > 0) {
            return this._getDeploymentListView(filteredDeployments);
        }

        return null;
    }

    public componentWillUnmount(): void {
        this._store.removeListener(WorkloadsEvents.ReplicaSetsFetchedEvent, this._onReplicaSetsFetched);
        this._imageDetailsStore.removeListener(ImageDetailsEvents.HasImageDetailsEvent, this._setHasImageDetails);
    }

    // deployments have already been populated in store by KubeSummary parent component
    private _onReplicaSetsFetched = (): void => {
        const storeState = this._store.getState();
        this.setState({
            deploymentList: storeState.deploymentList,
            replicaSetList: storeState.replicaSetList
        }, () => {
            this.props.markTTICallback && this.props.markTTICallback({ "component": "DeploymentTable" });
        });
    }

    private _setHasImageDetails = (): void => {
        this.setState({});
    }

    private _getDeploymentListView(filteredDeployments: V1Deployment[]) {
        let renderList: JSX.Element[] = [];
        DeploymentsTable._generateDeploymentReplicaSetMap(filteredDeployments, this.state.replicaSetList).forEach((entry, index) => {
            const items = DeploymentsTable._getDeploymentReplicaSetItems(entry.deployment, entry.replicaSets);
            const key = format("workloads-d-t-{0}", index);
            const tableProps = {
                id: key,
                role: "table",
                showHeader: true,
                showLines: true,
                singleClickActivation: true,
                itemProvider: new ArrayItemProvider<IDeploymentReplicaSetItem>(items),
                ariaLabel: entry.deployment.metadata.name,
                columns: this._getColumns(),
                onActivate: (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<any>) => {
                    const eventTarget = event && event.target as HTMLElement;
                    // make sure all links have this classname
                    if (eventTarget
                        && !eventTarget.classList.contains(this._podsLinkClassName)
                        && !eventTarget.classList.contains(this._imageLinkClassName)) {
                        this._openDeploymentItem(event, tableRow, items[tableRow.index]);
                    }
                }
            } as ITableProps<any>;
            const deploymentCard = (
                <CustomCard
                    className="deployment-replica-with-pod-list k8s-card-padding bolt-table-card flex-grow bolt-card-no-vertical-padding"
                    key={key}
                >
                    <CustomHeader>
                        <HeaderTitleArea>
                            <HeaderTitleRow
                                className="workloads-card-title">
                                <HeaderTitle
                                    className="workloads-card-header"
                                    titleSize={TitleSize.Medium}>
                                    <Tooltip text={entry.deployment.metadata.name} overflowOnly={true}>
                                        {DeploymentsTable._getHeadingContent(entry.deployment)}
                                    </Tooltip>
                                </HeaderTitle>
                                <Tags className="deployment-tbl-heading-labels body-s" tagContentClassName="workloads-card-tags" items={entry.deployment.metadata.labels} pillGroupOverflow={PillGroupOverflow.wrap} />
                            </HeaderTitleRow>
                            <HeaderDescription className="text-ellipsis">{Resources.DeploymentText}</HeaderDescription>
                        </HeaderTitleArea>
                    </CustomHeader>
                    <CardContent className="deployment-replicaset-table" contentPadding={false}>
                        <Table
                            {...tableProps}
                        />
                    </CardContent>
                </CustomCard>
            );

            renderList.push(deploymentCard);
        });

        return renderList;
    }

    private static _generateDeploymentReplicaSetMap(
        deploymentList: V1Deployment[],
        replicaSetList: V1ReplicaSetList | undefined): IDeploymentReplicaSetMap[] {
        let deploymentReplicaSetMap: IDeploymentReplicaSetMap[] = [];
        deploymentList.forEach(d => {
            const replicas = replicaSetList && replicaSetList.items || [];
            const filteredReplicas: V1ReplicaSet[] = replicas.filter(r => DeploymentsTable._isReplicaSetForDeployment(d, r)) || [];
            filteredReplicas.sort((a, b) => {
                // descending order
                return DeploymentsTable._getCreationTime(b.metadata) - DeploymentsTable._getCreationTime(a.metadata);
            });

            deploymentReplicaSetMap.push({
                deployment: d,
                replicaSets: filteredReplicas
            });
        });

        return deploymentReplicaSetMap;
    }

    private static _getDeploymentReplicaSetItems(deployment: V1Deployment, replicaSets: V1ReplicaSet[]):
        IDeploymentReplicaSetItem[] {
        let items: IDeploymentReplicaSetItem[] = [];
        replicaSets.forEach((replicaSet, index) => {
            if (replicaSet && replicaSet.spec && replicaSet.spec.replicas > 0) {
                items.push(DeploymentsTable._getDeploymentReplicaSetItem(deployment, replicaSet, index, replicaSets.length));
            }
        });

        return items;
    }

    private static _getDeploymentReplicaSetItem(
        deployment: V1Deployment,
        replica: V1ReplicaSet,
        index: number,
        replicaSetLength: number): IDeploymentReplicaSetItem {
        // todo :: annotations are taken from deployment for the first replica, rest of the replicas from respective replicas
        const annotations: {
            [key: string]: string;
        } = index === 0 ? deployment.metadata.annotations : replica.metadata.annotations;
        const podslist = StoreManager.GetStore<PodsStore>(PodsStore).getState().podsList;
        const pods: V1Pod[] = podslist && podslist.items || [];
        const imageId = Utils.getImageIdForWorkload(Utils.getFirstContainerName(replica.spec.template.spec), pods, replica.metadata.uid);
        if (imageId && (DeploymentsTable._imageList.length <= 0 || DeploymentsTable._imageList.findIndex(img => equals(img, imageId)) < 0)) {
            DeploymentsTable._imageList.push(imageId);
        }
        const { imageText, imageTooltipText } = Utils.getImageText(replica.spec.template.spec);

        return {
            name: index > 0 ? "" : deployment.metadata.name,
            deploymentId: deployment.metadata.uid,
            replicaSetId: replica.metadata.uid,
            replicaSetName: replica.metadata.name,
            pipeline: Utils.getPipelineText(annotations),
            showRowBorder: (replicaSetLength === (index + 1)),
            deployment: deployment,
            imageDisplayText: imageText,
            imageId: imageId,
            creationTimeStamp: replica.metadata.creationTimestamp,
            kind: replica.kind || "ReplicaSet",
            imageTooltip: imageTooltipText,
            // todo :: how to find error in replicaSet
            ...Utils.getPodsStatusProps(replica.status.readyReplicas, replica.status.replicas)
        };
    }

    private static _getCreationTime(metadata: V1ObjectMeta): number {
        return (!!metadata.creationTimestamp ? new Date(metadata.creationTimestamp) : new Date()).getTime();
    }

    private static _isReplicaSetForDeployment(deployment: V1Deployment, replica: V1ReplicaSet): boolean {
        return !!deployment && !!replica
            && replica.metadata.namespace.toLowerCase() === deployment.metadata.namespace.toLowerCase()
            && replica.metadata.ownerReferences && replica.metadata.ownerReferences.length > 0
            && replica.metadata.ownerReferences[0].uid.toLowerCase() === deployment.metadata.uid.toLowerCase();
    }

    private _getColumns = (): ITableColumn<IDeploymentReplicaSetItem>[] => {
        let columns: ITableColumn<IDeploymentReplicaSetItem>[] = [];
        columns.push({
            id: "replicasetName",
            name: Resources.ReplicaSetText,
            width: new ObservableValue(348),
            renderCell: DeploymentsTable._renderReplicaSetNameCell
        });
        columns.push({
            id: "imageName",
            name: Resources.ImageText,
            width: -72,
            renderCell: this._renderImageCell
        });
        columns.push({
            id: "pods",
            name: Resources.PodsText,
            width: new ObservableValue(140),
            renderCell: this._renderPodsCountCell
        });
        columns.push({
            id: "age",
            name: Resources.AgeText,
            width: -28,
            renderCell: DeploymentsTable._renderAgeCell
        });

        return columns;
    }

    private static _renderReplicaSetNameCell(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IDeploymentReplicaSetItem>, deployment: IDeploymentReplicaSetItem): JSX.Element {
        const itemToRender = defaultColumnRenderer(deployment.replicaSetName || "", "body-m font-weight-semibold");
        return renderTableCell(rowIndex, columnIndex, tableColumn, itemToRender);
    }

    private _renderPodsCountCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IDeploymentReplicaSetItem>, deployment: IDeploymentReplicaSetItem): JSX.Element => {
        const replicaSet = this._getSelectedReplicaSet(deployment);
        const podslist = StoreManager.GetStore<PodsStore>(PodsStore).getState().podsList;
        const relevantPods = (podslist && podslist.items && podslist.items.filter(p => (replicaSet && Utils.isOwnerMatched(p.metadata, replicaSet.metadata.uid)) || false))

        const _onPodsClicked = (relevantPods && replicaSet) ? (() => onPodsColumnClicked(relevantPods, replicaSet, "ReplicaSet", this._selectionActionCreator)) : undefined;

        return renderPodsStatusTableCell(rowIndex, columnIndex, tableColumn, deployment.pods, deployment.statusProps, deployment.podsTooltip, _onPodsClicked, this._podsLinkClassName);
    }

    private _renderImageCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IDeploymentReplicaSetItem>, deployment: IDeploymentReplicaSetItem): JSX.Element => {
        const textToRender: string = deployment.imageDisplayText || "";
        const imageId: string = deployment.imageId;
        let imageDetailsUnavailableTooltipText = "";
        const hasImageDetails: boolean | undefined = this._imageDetailsStore.hasImageDetails(imageId);
        // If hasImageDetails is undefined, then image details promise has not resolved, so do not set imageDetailsUnavailable tooltip
        if (hasImageDetails === false) {
            imageDetailsUnavailableTooltipText = localeFormat("{0} | {1}", deployment.imageTooltip || textToRender, Resources.ImageDetailsUnavailableText);
        }

        const itemToRender = hasImageDetails ?
            <Tooltip overflowOnly>
                <Link
                    className={css("body-m text-ellipsis bolt-table-link", this._imageLinkClassName)}
                    rel={"noopener noreferrer"}
                    excludeTabStop
                    onClick={(e) => {
                        e.preventDefault();
                        this._onImageClick(imageId);
                    }}
                >
                    {textToRender}
                </Link>
            </Tooltip >
            : defaultColumnRenderer(textToRender, "body-m", imageDetailsUnavailableTooltipText);

        return renderTableCell(rowIndex, columnIndex, tableColumn, itemToRender, undefined, hasImageDetails ? "bolt-table-cell-content-with-link" : "");
    }

    private static _renderAgeCell(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IDeploymentReplicaSetItem>, deployment: IDeploymentReplicaSetItem): JSX.Element {
        const createdDate = deployment.creationTimeStamp ? deployment.creationTimeStamp : new Date();
        const itemToRender = <Ago className="body-m" date={new Date(createdDate)} />;
        return renderTableCell(rowIndex, columnIndex, tableColumn, itemToRender);
    }

    private static _getHeadingContent(deployment: V1Deployment): JSX.Element {
        return (
            <div>
                {deployment.metadata.name}
            </div>
        );
    }

    private _openDeploymentItem = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<any>, selectedItem: IDeploymentReplicaSetItem) => {
        const selectedReplicaSet = this._getSelectedReplicaSet(selectedItem);
        if (selectedReplicaSet) {
            const payload: ISelectionPayload = {
                item: selectedReplicaSet,
                itemUID: selectedReplicaSet.metadata.uid,
                showSelectedItem: true,
                selectedItemType: SelectedItemKeys.ReplicaSetKey
            };

            this._selectionActionCreator.selectItem(payload);
        }
    }

    private _getSelectedReplicaSet(selectedItem: IDeploymentReplicaSetItem): V1ReplicaSet | null {
        // have one selection store, raise action to send selectedItem, in the store
        if (selectedItem && selectedItem.replicaSetId) {
            const selectedItemReplicaSetUId: string = selectedItem.replicaSetId.toLowerCase();
            const replicas = this.state.replicaSetList;
            const filteredReplicas = (replicas && replicas.items || []).filter(r => {
                return r.metadata.uid.toLowerCase() === selectedItemReplicaSetUId;
            });

            return filteredReplicas[0];
        }

        return null;
    }

    private _onImageClick = (imageId: string): void => {
        const showImageDetails = (imageDetails: IImageDetails): void => {
            const payload: ISelectionPayload = {
                item: imageDetails,
                itemUID: "",
                showSelectedItem: true,
                selectedItemType: SelectedItemKeys.ImageDetailsKey
            };
            this._selectionActionCreator.selectItem(payload);
        };
        ActionsCreatorManager.GetActionCreator<ImageDetailsActionsCreator>(ImageDetailsActionsCreator).getImageDetails(imageId, showImageDetails);
    }

    private _podsLinkClassName = "d-pods-link";
    private _imageLinkClassName = "d-image-link";
    private _store: WorkloadsStore;
    private _workloadsActionCreator: WorkloadsActionsCreator;
    private _selectionActionCreator: SelectionActionsCreator;
    private _imageDetailsStore: ImageDetailsStore;
    private static _imageList: string[] = [];
}
